import { type FC, useEffect, useCallback } from 'react';
import { useFragment, useRelayEnvironment, graphql } from 'react-relay';
import { FormattedMessage } from 'dibs-react-intl';
import { trackEvent } from 'dibs-tracking';
import { NftTermsOfUseLink } from 'dibs-crypto-utils/exports/NftTermsOfUseLink';
import { NftFaqLink } from './NftFaqLink';
import { useWeb3Connection } from 'dibs-crypto-utils/exports/useWeb3Connection';
import { WithWalletConnectionEnsuredModalBase } from './WithWalletConnectionEnsuredModalBase';
import { alreadyConnectedMessage } from '../EthereumConnectionAlreadyConnectedModal';
import { type ConnectWalletWithSigningNoZipContent_viewer$key } from './__generated__/ConnectWalletWithSigningNoZipContent_viewer.graphql';

export const ConnectWalletWithSigningNoZipContent: FC<{
    userId: string;
    trackingLabel: string;
    viewer: ConnectWalletWithSigningNoZipContent_viewer$key;
    onWalletPersisted: () => void;
}> = ({ userId, trackingLabel, viewer: viewerRef, onWalletPersisted }) => {
    const environment = useRelayEnvironment();
    const { initiateConnection, connected } = useWeb3Connection();

    const viewer = useFragment(
        graphql`
            fragment ConnectWalletWithSigningNoZipContent_viewer on Viewer {
                ...WithWalletConnectionEnsuredModalBase_viewer
            }
        `,
        viewerRef
    );

    useEffect(() => {
        trackEvent({
            event: 'e_ga_event',
            category: 'NFT',
            action: 'metamask connect entry',
            label: trackingLabel,
        });
    }, [trackingLabel]);

    const handleConnect = useCallback(
        async (walletType: string) => {
            await initiateConnection({
                environment,
                userId,
                zipCode: null,
                walletType,
                onSign: () => {
                    trackEvent({
                        event: 'e_ga_event',
                        category: 'NFT',
                        action: 'metamask connect complete',
                        label: `connect - ${trackingLabel}`,
                    });

                    onWalletPersisted();
                },
            });
        },
        [environment, initiateConnection, userId, trackingLabel, onWalletPersisted]
    );

    let errorMessage;
    if (connected.alreadyConnected) {
        errorMessage = alreadyConnectedMessage;
    }
    return (
        <WithWalletConnectionEnsuredModalBase
            title={
                <FormattedMessage
                    id="nft.withWalletConnection.connectMetamaskNoZIp"
                    defaultMessage="Connect Your Wallet"
                />
            }
            paragraphs={[
                <FormattedMessage
                    id="nft.withWalletConnection.connectMetamaskNoZIp.bodyCopy1"
                    defaultMessage="In order to proceed, you will need to connect your wallet. By connecting your wallet, you agree that you have read and accepted {termsOfUse}."
                    values={{
                        termsOfUse: <NftTermsOfUseLink />,
                    }}
                />,
            ]}
            showWalletButtons
            footer={<NftFaqLink />}
            onMetamaskConnect={() => handleConnect('metamask')}
            onWalletConnect={() => handleConnect('walletconnect')}
            viewer={viewer}
            errorMessage={errorMessage}
        />
    );
};
